/**
 * AdminCustomers — CRUD module for managing non-admin signed-up customers.
 *
 * Features:
 *  - Real-time list of customers from the `users` collection (isCustomer == true)
 *  - Search by name or email
 *  - Filter by status (all / active / disabled)
 *  - View / edit customer details (notes, active status)
 *  - Delete customer record (removes admin metadata; subcollections untouched)
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  collection,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  orderBy,
  where,
} from 'firebase/firestore';
import { db, appId } from '@/lib/firebase';
import {
  Users,
  Search,
  Pencil,
  Trash2,
  ArrowLeft,
  Mail,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  X,
  Save,
} from 'lucide-react';

export interface CustomerRecord {
  id: string;
  uid: string;
  email: string;
  displayName: string;
  photoUrl: string | null;
  signInMethod: 'google' | 'email' | 'unknown';
  isActive: boolean;
  registeredAt: { toDate: () => Date } | null;
  lastSignInAt: { toDate: () => Date } | null;
  notes: string;
}

interface AdminCustomersProps {
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
  confirmAction?: (title: string, message: string, action: () => void) => void;
}

type View = 'list' | 'edit';
type StatusFilter = 'all' | 'active' | 'disabled';

function formatDate(ts: { toDate: () => Date } | null): string {
  if (!ts) return '—';
  try {
    return ts.toDate().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function formatDateTime(ts: { toDate: () => Date } | null): string {
  if (!ts) return '—';
  try {
    const d = ts.toDate();
    return (
      d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' · ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    );
  } catch {
    return '—';
  }
}

function CustomerAvatar({
  customer,
  size = 'md',
}: {
  customer: CustomerRecord;
  size?: 'sm' | 'md' | 'lg';
}) {
  const sizeClass = size === 'sm' ? 'w-8 h-8 text-xs' : size === 'lg' ? 'w-12 h-12 text-base' : 'w-9 h-9 text-sm';
  const initials = customer.displayName
    ? customer.displayName.charAt(0).toUpperCase()
    : customer.email.charAt(0).toUpperCase();

  if (customer.photoUrl) {
    return (
      <img
        src={customer.photoUrl}
        alt={customer.displayName || customer.email}
        className={`${sizeClass} rounded-full object-cover shrink-0 ring-2 ring-white`}
      />
    );
  }
  return (
    <div
      className={`${sizeClass} rounded-full bg-blue-100 text-blue-700 font-semibold flex items-center justify-center shrink-0`}
    >
      {initials}
    </div>
  );
}

export function AdminCustomers({ showNotify, confirmAction }: AdminCustomersProps): JSX.Element {
  const [customers, setCustomers] = useState<CustomerRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('list');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerRecord | null>(null);

  // Edit form state
  const [editNotes, setEditNotes] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  // List filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const unsubRef = useRef<(() => void) | null>(null);

  const notify = useCallback(
    (msg: string, type: 'success' | 'error' | 'info' = 'info') => {
      showNotify?.(msg, type);
    },
    [showNotify]
  );

  useEffect(() => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    const usersRef = collection(db, 'artifacts', appId, 'users');
    const q = query(usersRef, where('isCustomer', '==', true), orderBy('registeredAt', 'desc'));

    setLoading(true);
    unsubRef.current = onSnapshot(
      q,
      (snapshot) => {
        const list: CustomerRecord[] = [];
        snapshot.forEach((d) => {
          list.push({ id: d.id, ...(d.data() as Omit<CustomerRecord, 'id'>) });
        });
        setCustomers(list);
        setLoading(false);
      },
      (err) => {
        console.error('[AdminCustomers] Failed to load customers from users collection:', err);
        notify('Failed to load customers. Please refresh.', 'error');
        setLoading(false);
      }
    );

    return () => {
      unsubRef.current?.();
    };
  }, [notify]);

  // ---- Filtered list -------------------------------------------------------

  const filtered = customers.filter((c) => {
    const matchesSearch =
      !search ||
      c.displayName.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && c.isActive) ||
      (statusFilter === 'disabled' && !c.isActive);
    return matchesSearch && matchesStatus;
  });

  // ---- Edit view -----------------------------------------------------------

  function openEdit(customer: CustomerRecord) {
    setSelectedCustomer(customer);
    setEditNotes(customer.notes || '');
    setEditIsActive(customer.isActive);
    setView('edit');
  }

  function goToList() {
    setView('list');
    setSelectedCustomer(null);
    setEditNotes('');
    setEditIsActive(true);
  }

  async function handleSave() {
    if (!selectedCustomer) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'users', selectedCustomer.id), {
        notes: editNotes.trim(),
        isActive: editIsActive,
        updatedAt: serverTimestamp(),
      });
      notify('Customer updated successfully', 'success');
      goToList();
    } catch (err) {
      console.error('[AdminCustomers] Failed to update customer record:', err);
      notify('Failed to update customer. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  // ---- Toggle active -------------------------------------------------------

  async function handleToggleActive(customer: CustomerRecord) {
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'users', customer.id), {
        isActive: !customer.isActive,
        updatedAt: serverTimestamp(),
      });
      notify(
        customer.isActive ? 'Customer account disabled' : 'Customer account activated',
        'success'
      );
    } catch (err) {
      console.error('[AdminCustomers] Failed to toggle customer active state:', err);
      notify('Failed to update customer status.', 'error');
    }
  }

  // ---- Delete --------------------------------------------------------------

  function handleDelete(customer: CustomerRecord) {
    const doDelete = async () => {
      try {
        await deleteDoc(doc(db, 'artifacts', appId, 'users', customer.id));
        notify('Customer record deleted', 'success');
        if (view === 'edit') goToList();
      } catch (err) {
        console.error('[AdminCustomers] Failed to delete customer record:', err);
        notify('Failed to delete customer.', 'error');
      }
    };

    if (confirmAction) {
      confirmAction(
        'Delete Customer',
        `Are you sure you want to delete the record for "${customer.displayName || customer.email}"? This removes the admin record only — it does not delete their Firebase Auth account.`,
        () => void doDelete()
      );
    } else {
      void doDelete();
    }
  }

  // ---- Render: Edit view ---------------------------------------------------

  if (view === 'edit' && selectedCustomer) {
    return (
      <div className="fixed inset-0 left-0 lg:left-64 z-30 flex flex-col bg-white">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-200 bg-white shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={goToList}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 shrink-0"
            >
              <ArrowLeft size={15} aria-hidden="true" />
              Back to Customers
            </button>
            <div className="h-5 w-px bg-gray-200 shrink-0" />
            <h2 className="text-base font-semibold text-gray-900 truncate">
              {selectedCustomer.displayName || selectedCustomer.email}
            </h2>
          </div>
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shrink-0"
          >
            <Save size={15} aria-hidden="true" />
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

        {/* Two-column body */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left sidebar — metadata + actions */}
          <aside className="w-72 shrink-0 border-r border-gray-200 bg-gray-50 overflow-y-auto p-5 flex flex-col gap-5">
            {/* Avatar + name */}
            <div className="flex flex-col items-center text-center gap-3 pt-2">
              <CustomerAvatar customer={selectedCustomer} size="lg" />
              <div>
                <p className="font-semibold text-gray-900 text-sm">
                  {selectedCustomer.displayName || '(no name)'}
                </p>
                <p className="text-xs text-gray-500 break-all">{selectedCustomer.email}</p>
              </div>
            </div>

            {/* Info fields */}
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Account Info
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <Calendar size={14} className="text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <span className="block text-xs text-gray-400">Registered</span>
                    <span className="text-gray-700">{formatDate(selectedCustomer.registeredAt)}</span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Clock size={14} className="text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <span className="block text-xs text-gray-400">Last Sign-in</span>
                    <span className="text-gray-700">
                      {formatDateTime(selectedCustomer.lastSignInAt)}
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Mail size={14} className="text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <span className="block text-xs text-gray-400">Sign-in Method</span>
                    <span className="capitalize text-gray-700">
                      {selectedCustomer.signInMethod}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Active status toggle */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</p>
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={editIsActive}
                  onChange={(e) => setEditIsActive(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Account is active</span>
              </label>
            </div>

            {/* Delete — pinned to bottom */}
            <div className="mt-auto pt-4 border-t border-gray-200">
              <button
                onClick={() => handleDelete(selectedCustomer)}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
              >
                <Trash2 size={15} aria-hidden="true" />
                Delete Customer
              </button>
            </div>
          </aside>

          {/* Right panel — notes */}
          <div className="flex flex-col flex-1 min-w-0 overflow-y-auto p-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Admin Notes
            </p>
            <textarea
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="Add internal notes about this customer (visible to admins only)…"
              className="w-full flex-1 min-h-[200px] px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={10}
            />
            <p className="mt-2 text-xs text-gray-400">
              Notes are internal only — customers cannot see this field.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ---- Render: List view ---------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users size={22} className="text-blue-600" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Customers</h1>
            <p className="text-sm text-gray-500">
              Manage non-admin accounts that have signed up.
            </p>
          </div>
        </div>
        {!loading && (
          <span className="text-sm text-gray-400 shrink-0">
            {customers.length} customer{customers.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Search + filter bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            aria-hidden="true"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Clear search"
            >
              <X size={15} />
            </button>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Filter by status"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse h-16 bg-gray-100 rounded-xl" />
          ))}
        </div>
      ) : customers.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center py-16 px-4 text-center">
          <Users size={40} className="text-gray-300 mb-3" aria-hidden="true" />
          <p className="text-sm font-medium text-gray-500">No customers yet</p>
          <p className="text-xs text-gray-400 mt-1">
            Customers will appear here once users sign up on the site.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-gray-50 flex flex-col items-center justify-center py-12 px-4 text-center">
          <Search size={32} className="text-gray-300 mb-3" aria-hidden="true" />
          <p className="text-sm font-medium text-gray-500">No customers match your search</p>
          <button
            onClick={() => { setSearch(''); setStatusFilter('all'); }}
            className="mt-3 text-xs text-blue-600 hover:underline"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
          {/* Table header */}
          <div className="hidden sm:grid sm:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_120px_120px_100px] gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            <span>Customer</span>
            <span>Email</span>
            <span>Registered</span>
            <span>Last Sign-in</span>
            <span className="text-right">Actions</span>
          </div>

          {/* Rows */}
          <div className="divide-y divide-gray-100">
            {filtered.map((customer) => (
              <div
                key={customer.id}
                className="flex flex-col sm:grid sm:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_120px_120px_100px] gap-2 sm:gap-4 px-4 py-3 items-start sm:items-center hover:bg-gray-50 transition-colors group"
              >
                {/* Customer name + avatar */}
                <div className="flex items-center gap-3 min-w-0">
                  <CustomerAvatar customer={customer} size="sm" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {customer.displayName || '(no name)'}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${
                          customer.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {customer.isActive ? (
                          <CheckCircle size={10} aria-hidden="true" />
                        ) : (
                          <XCircle size={10} aria-hidden="true" />
                        )}
                        {customer.isActive ? 'Active' : 'Disabled'}
                      </span>
                      <span className="text-xs text-gray-400 capitalize">
                        {customer.signInMethod}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="min-w-0 pl-11 sm:pl-0">
                  <a
                    href={`mailto:${customer.email}`}
                    className="text-sm text-blue-600 hover:underline truncate block"
                  >
                    {customer.email}
                  </a>
                </div>

                {/* Registered */}
                <div className="hidden sm:block text-sm text-gray-500 tabular-nums">
                  {formatDate(customer.registeredAt)}
                </div>

                {/* Last sign-in */}
                <div className="hidden sm:block text-sm text-gray-500 tabular-nums">
                  {formatDate(customer.lastSignInAt)}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 justify-end sm:justify-end pl-11 sm:pl-0">
                  <button
                    onClick={() => void handleToggleActive(customer)}
                    className={`p-1.5 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                      customer.isActive
                        ? 'text-gray-400 hover:text-orange-500 hover:bg-orange-50'
                        : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                    }`}
                    title={customer.isActive ? 'Disable account' : 'Enable account'}
                    aria-label={customer.isActive ? 'Disable account' : 'Enable account'}
                  >
                    {customer.isActive ? (
                      <XCircle size={15} aria-hidden="true" />
                    ) : (
                      <CheckCircle size={15} aria-hidden="true" />
                    )}
                  </button>
                  <button
                    onClick={() => openEdit(customer)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
                    title="Edit customer"
                    aria-label="Edit customer"
                  >
                    <Pencil size={15} aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => handleDelete(customer)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
                    title="Delete customer"
                    aria-label="Delete customer"
                  >
                    <Trash2 size={15} aria-hidden="true" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Footer count */}
          {filtered.length < customers.length && (
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-400">
              Showing {filtered.length} of {customers.length} customers
            </div>
          )}
        </div>
      )}
    </div>
  );
}
